import React, { useState } from "react";
import Papa from "papaparse";
import apiInstance from "../../../utility/apiInstance";

interface BulkUploadPopupProps {
  show: boolean;
  onClose: () => void;
  institutionId: string;
  onBulkUploadComplete: (programs: any[]) => void;
}

const BulkUploadPopup: React.FC<BulkUploadPopupProps> = ({
  show,
  onClose,
  institutionId,
  onBulkUploadComplete,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [completeData, setcompleteData] = useState<any[]>([]);

  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({
    error: false,
    loading: false,
    text: "",
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      setMsg({
        error: true,
        loading: false,
        text: "Please upload a CSV file",
      });
      return;
    }

    setFile(selectedFile);
    setMsg({ error: false, loading: false, text: "" });

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        const cleanedData = results.data.map((row: any) => {
          const cleanRow: any = {};
          Object.keys(row).forEach((key) => {
            cleanRow[key.trim()] = row[key];
          });
          return cleanRow;
        });
        setPreview(cleanedData.slice(0, 5));
        setcompleteData(cleanedData);
      },
      error: (error) => {
        setMsg({
          error: true,
          loading: false,
          text: `CSV parsing error: ${error.message}`,
        });
      },
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setMsg({
        error: true,
        loading: false,
        text: "Please select a file first",
      });
      return;
    }

    setMsg({
      error: false,
      loading: true,
      text: "Processing CSV...",
    });

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: async (results) => {
        try {
          // Trim whitespace from headers
          const programs = results.data.map((row: any) => {
            const cleanRow: any = {};
            Object.keys(row).forEach((key) => {
              cleanRow[key.trim()] = row[key];
            });
            return cleanRow;
          });

          setMsg({
            error: false,
            loading: true,
            text: `Uploading ${programs.length} programs...`,
          });

          const uploadedPrograms = [];
          let successCount = 0;
          let failCount = 0;

          for (let i = 0; i < programs.length; i++) {
            const program = programs[i];
            
            try {
              const programData = {
                programName: program.programName || program["Program Name"],
                about: program.about || program.About || "",
                requirement: program.requirement || program.Requirement || "",
                institution: institutionId,
                applicationFee: parseFloat(program.applicationFee || program["Application Fee"] || 0),
                tuitionFee: program.tuitionFee || program["Tuition Fee"] || program.tuitionFee || "",
                programLevel: program.programLevel || program["Program Level"] || "undergraduate",
                field: program.field || program["Field of Study"] || "arts",
                start: program.start || program["Start Date"] || new Date(),
                end: program.end || program["End Date"] || new Date(),
              };

              const response = await apiInstance.post("/api/programs", programData);

              if (response) {

                const data = await response.data;
                uploadedPrograms.push(data.doc);
                successCount++;
              } else {
                failCount++;
              }

              setMsg({
                error: false,
                loading: true,
                text: `Uploaded ${i + 1}/${programs.length} programs...`,
              });
            } catch (error) {
              console.error(`Failed to upload program: ${program.programName}`, error);
              failCount++;
            }
          }

          setMsg({
            error: failCount > 0,
            loading: false,
            text: `Upload complete! Success: ${successCount}, Failed: ${failCount}`,
          });

          if (uploadedPrograms.length > 0) {
            onBulkUploadComplete(uploadedPrograms);
          }

          setTimeout(() => {
            if (failCount === 0) {
              onClose();
            }
          }, 3000);
        } catch (error: any) {
          setMsg({
            error: true,
            loading: false,
            text: `Upload failed: ${error.message}`,
          });
        }
      },
      error: (error) => {
        setMsg({
          error: true,
          loading: false,
          text: `CSV parsing error: ${error.message}`,
        });
      },
    });
  };

  const downloadTemplate = () => {
    const template = `programName,about,requirement,applicationFee,tuitionFee,programLevel,field,start,end
Bachelor of Computer Science,Comprehensive computer science program,High school diploma with Math,100,15000,undergraduate,tech,2025-09-01,2029-06-30
Master of Business Administration,Advanced business management program,Bachelor's degree in any field,150,20000,postgraduate,business,2025-08-15,2027-05-30`;

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "program_upload_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title text-secondary">
              Bulk Upload Programs
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={uploading}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="card mb-4 shadow-sm border-0 rounded-3">
              <div className="card-body">
                {/* Instructions */}
                <div className="alert alert-info mb-4">
                  <h6 className="fw-bold mb-2">Instructions:</h6>
                  <ol className="mb-0 ps-3">
                    <li>Download the CSV template below</li>
                    <li>Fill in your program data following the template format</li>
                    <li>Upload the completed CSV file</li>
                    <li>Review the preview and click "Upload Programs"</li>
                  </ol>
                  <button
                    className="btn btn-sm btn-outline-primary mt-3"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-download me-2"></i>
                    Download CSV Template
                  </button>
                </div>

                {/* File Upload */}
                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Select CSV File <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    className="form-control"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  {file && (
                    <small className="text-muted d-block mt-2">
                      Selected: {file.name}
                    </small>
                  )}
                </div>

                {/* Preview Table */}
                {preview.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Preview (First 5 rows):</h6>
                    <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <table className="table table-bordered table-sm">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Program Name</th>
                            <th>Level</th>
                            <th>Field</th>
                            <th>App Fee</th>
                            <th>Tuition Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.map((row, index) => (
                            <tr key={index}>
                              <td>{row.programName || row["Program Name"] || "-"}</td>
                              <td>{row.programLevel || row["Program Level"] || "-"}</td>
                              <td>{row.field || row["Field of Study"] || "-"}</td>
                              <td>${row.applicationFee || row["Application Fee"] || 0}</td>
                              <td>${row.tuitionFee || row["Tuition Fee"] || row.tuitionFee || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <small className="text-muted">
                      Total programs in file: {completeData.length}
                    </small>
                  </div>
                )}

                {/* CSV Format Requirements */}
                <div className="alert alert-warning">
                  <h6 className="fw-bold mb-2">Required CSV Columns:</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <ul className="mb-0 ps-3 small">
                        <li>programName (required)</li>
                        <li>about</li>
                        <li>requirement</li>
                        <li>applicationFee (number)</li>
                        <li>tuitionFee </li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <ul className="mb-0 ps-3 small">
                        <li>programLevel (undergraduate/postgraduate/diploma/school/language)</li>
                        <li>field (arts/business/tech/medicine/law/science/other)</li>
                        <li>start (YYYY-MM-DD)</li>
                        <li>end (YYYY-MM-DD)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <div className="w-100">
              {msg.text && (
                <div
                  className={`alert ${
                    msg.error ? "alert-danger" : "alert-success"
                  } mb-3`}
                >
                  {msg.text}
                </div>
              )}
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload me-2"></i>
                      Upload Programs
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadPopup;